
import { useEffect } from "react";
import { Routes, Route, useParams, useNavigate, useLocation } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";

// Page imports
import Index from "@/pages/Index";
import Gallery from "@/pages/Gallery";
import Equipment from "@/pages/Equipment";
import Booking from "@/pages/Booking";
import Contact from "@/pages/Contact";
import Surroundings from "@/pages/Surroundings";
import Access from "@/pages/Access";
import Legal from "@/pages/Legal";
import NotFound from "@/pages/NotFound";
import UserReservations from "@/pages/UserReservations";
import Profile from "@/pages/Profile";
import ReservationSuccess from "@/pages/ReservationSuccess";

// Lazy load Activities page
import Activities from "@/pages/Activities";

// Route mapping from language-specific to internal routes
const routeMappings: Record<string, Record<string, string>> = {
  en: {
    "": "/",
    "gallery": "/galerie",
    "equipment": "/equipements",
    "activities": "/activites",
    "book-now": "/reserver",
    "contact": "/contact",
    "surroundings": "/environs",
    "access": "/acces",
    "legal": "/mentions-legales",
    "my-reservations": "/mes-reservations",
    "profile": "/profil",
    "reservation-success": "/reservation-success",
  },
  es: {
    "": "/",
    "galeria": "/galerie",
    "equipamiento": "/equipements",
    "actividades": "/activites",
    "reservar": "/reserver",
    "contacto": "/contact",
    "alrededores": "/environs",
    "acceso": "/acces",
    "aviso-legal": "/mentions-legales",
    "mis-reservas": "/mes-reservations",
    "perfil": "/profil",
    "reserva-exitosa": "/reservation-success",
  },
  it: {
    "": "/",
    "galleria": "/galerie",
    "attrezzature": "/equipements",
    "attivita": "/activites",
    "prenota": "/reserver",
    "contatto": "/contact",
    "dintorni": "/environs",
    "accesso": "/acces",
    "note-legali": "/mentions-legales",
    "le-mie-prenotazioni": "/mes-reservations",
    "profilo": "/profil",
    "prenotazione-successo": "/reservation-success",
  },
  de: {
    "": "/",
    "galerie": "/galerie",
    "ausstattung": "/equipements",
    "aktivitaeten": "/activites",
    "buchen": "/reserver",
    "kontakt": "/contact",
    "umgebung": "/environs",
    "anfahrt": "/acces",
    "impressum": "/mentions-legales",
    "meine-buchungen": "/mes-reservations",
    "profil": "/profil",
    "buchung-erfolg": "/reservation-success",
  },
  nl: {
    "": "/",
    "galerij": "/galerie",
    "uitrusting": "/equipements",
    "activiteiten": "/activites",
    "boeken": "/reserver",
    "contact": "/contact",
    "omgeving": "/environs",
    "toegang": "/acces",
    "juridische-kennisgeving": "/mentions-legales",
    "mijn-reserveringen": "/mes-reservations",
    "profiel": "/profil",
    "reservering-succes": "/reservation-success",
  }
};

// Inverse mapping from internal routes to language-specific routes
const inverseRouteMappings: Record<string, Record<string, string>> = {};

// Initialize inverse mappings
Object.entries(routeMappings).forEach(([lang, routes]) => {
  inverseRouteMappings[lang] = {};
  Object.entries(routes).forEach(([localized, internal]) => {
    inverseRouteMappings[lang][internal] = localized;
  });
});

/**
 * Get localized path for a given route
 */
export const getLocalizedPath = (path: string, language: string): string => {
  if (language === 'fr') {
    return path;
  }
  
  const mapping = inverseRouteMappings[language];
  if (!mapping) return path;
  
  // Find the matching route
  for (const [internal, localized] of Object.entries(mapping)) {
    if (path === internal || path.startsWith(`${internal}/`)) {
      if (localized === "") {
        return `/${language}${path.substring(internal.length)}`;
      }
      return `/${language}/${localized}${path.substring(internal.length)}`;
    }
  }
  
  return path;
};

const LocalizedRoute = () => {
  const { lang } = useParams<{ lang: string }>();
  const { setLanguage, language } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Set language based on route parameter
    if (lang && ['en', 'es', 'it', 'de', 'nl'].includes(lang)) {
      // Always set language from URL parameter
      if (lang !== language) {
        setLanguage(lang as any);
      }
    } else if (lang && !['en', 'es', 'it', 'de', 'nl', 'fr'].includes(lang)) {
      // Invalid language code, redirect to 404
      navigate('/not-found', { replace: true });
    } else if (!lang && language !== 'fr') {
      // On French route without prefix, ensure language is French
      setLanguage('fr');
    }
  }, [lang, location.pathname]);
  
  // Extract the path after the language code
  const path = location.pathname.substring(lang ? `/${lang}`.length : 0);
  const cleanPath = path === "" ? "" : path.startsWith("/") ? path.substring(1) : path;
  
  // Handle cases where the path doesn't match any language-specific route
  if (lang && !['en', 'es', 'it', 'de', 'nl'].includes(lang)) {
    return <NotFound />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<Index />} />
      <Route path="/gallery" element={<Gallery />} />
      <Route path="/galeria" element={<Gallery />} />
      <Route path="/galleria" element={<Gallery />} />
      <Route path="/galerie" element={<Gallery />} />
      <Route path="/galerij" element={<Gallery />} />
      
      <Route path="/equipment" element={<Equipment />} />
      <Route path="/equipamiento" element={<Equipment />} />
      <Route path="/attrezzature" element={<Equipment />} />
      <Route path="/ausstattung" element={<Equipment />} />
      <Route path="/uitrusting" element={<Equipment />} />
      
      <Route path="/book-now" element={<Booking />} />
      <Route path="/reservar" element={<Booking />} />
      <Route path="/prenota" element={<Booking />} />
      <Route path="/buchen" element={<Booking />} />
      <Route path="/boeken" element={<Booking />} />
      
      <Route path="/contact" element={<Contact />} />
      <Route path="/contacto" element={<Contact />} />
      <Route path="/contatto" element={<Contact />} />
      <Route path="/kontakt" element={<Contact />} />
      
      <Route path="/surroundings" element={<Surroundings />} />
      <Route path="/alrededores" element={<Surroundings />} />
      <Route path="/dintorni" element={<Surroundings />} />
      <Route path="/umgebung" element={<Surroundings />} />
      <Route path="/omgeving" element={<Surroundings />} />
      
      <Route path="/activities" element={<Activities />} />
      <Route path="/actividades" element={<Activities />} />
      <Route path="/attivita" element={<Activities />} />
      <Route path="/aktivitaeten" element={<Activities />} />
      <Route path="/activiteiten" element={<Activities />} />
      
      <Route path="/access" element={<Access />} />
      <Route path="/acceso" element={<Access />} />
      <Route path="/accesso" element={<Access />} />
      <Route path="/anfahrt" element={<Access />} />
      <Route path="/toegang" element={<Access />} />
      
      <Route path="/legal" element={<Legal />} />
      <Route path="/aviso-legal" element={<Legal />} />
      <Route path="/note-legali" element={<Legal />} />
      <Route path="/impressum" element={<Legal />} />
      <Route path="/juridische-kennisgeving" element={<Legal />} />
      
      <Route path="/my-reservations" element={<UserReservations />} />
      <Route path="/mis-reservas" element={<UserReservations />} />
      <Route path="/le-mie-prenotazioni" element={<UserReservations />} />
      <Route path="/meine-buchungen" element={<UserReservations />} />
      <Route path="/mijn-reserveringen" element={<UserReservations />} />
      
      <Route path="/profile" element={<Profile />} />
      <Route path="/perfil" element={<Profile />} />
      <Route path="/profilo" element={<Profile />} />
      <Route path="/profil" element={<Profile />} />
      
      <Route path="/reservation-success" element={<ReservationSuccess />} />
      <Route path="/reserva-exitosa" element={<ReservationSuccess />} />
      <Route path="/prenotazione-successo" element={<ReservationSuccess />} />
      <Route path="/buchung-erfolg" element={<ReservationSuccess />} />
      <Route path="/reservering-succes" element={<ReservationSuccess />} />
      
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
};

export default LocalizedRoute;
